import { useEffect, useMemo, useState } from "react";
import { Mail, RefreshCw, Filter, Zap, Loader2 } from "lucide-react";
import AppShell, { getActiveSchemeId } from "@/components/AppShell";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";

// Sprint P2-S1 — Email inbox (minimal MVP).
//
// Category filter chips + threads list. Detail panel + Take-Action dropdown
// intentionally deferred to iteration — the backend endpoints exist and
// e1_tester can hit them directly via curl.

const CATEGORY_CHIPS = [
  { key: "",                    label: "All" },
  { key: "UNCATEGORISED",       label: "Uncategorised" },
  { key: "MAINTENANCE",         label: "Maintenance" },
  { key: "LEVY_QUERY",          label: "Levy" },
  { key: "INSURANCE",           label: "Insurance" },
  { key: "COMPLAINT",           label: "Complaint" },
  { key: "LEGAL",               label: "Legal" },
  { key: "CONTRACTOR_INVOICE",  label: "Contractor" },
  { key: "GOVERNMENT",          label: "Government" },
  { key: "AGM_RSVP",            label: "AGM RSVP" },
];

const CATEGORY_COLOR = {
  MAINTENANCE:         "#1e40af",
  LEVY_QUERY:          "#166534",
  INSURANCE:           "#7c2d12",
  COMPLAINT:           "#991b1b",
  LEGAL:               "#7c3aed",
  CONTRACTOR_INVOICE:  "#0369a1",
  GOVERNMENT:          "#0f766e",
  AGM_RSVP:            "#a16207",
  UNCATEGORISED:       "#475569",
};

export default function EmailInbox() {
  const schemeId = getActiveSchemeId();
  const [category, setCategory] = useState("");
  const [payload, setPayload] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [notConnected, setNotConnected] = useState(false);

  const load = async () => {
    if (!schemeId) return;
    try {
      const q = category ? `?category=${category}` : "";
      const { data } = await api.get(`/schemes/${schemeId}/emails${q}`);
      setPayload(data);
    } catch (e) { toast.error(apiError(e)); }
  };

  const checkStatus = async () => {
    if (!schemeId) return;
    try {
      const { data } = await api.get(`/connectors/gmail/status?scheme_id=${schemeId}`);
      setNotConnected(data.status !== "CONNECTED");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.error === "gmail_credentials_missing") {
        setNotConnected(true);
      }
    }
  };

  useEffect(() => { checkStatus(); load(); }, [schemeId, category]);

  const doSync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post(`/schemes/${schemeId}/emails/sync-now`);
      toast.success(`Sync complete — ingested ${data.ingested || 0} new · updated ${data.updated || 0}`);
      await load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSyncing(false); }
  };

  const total = payload?.total ?? 0;
  const counts = payload?.counts || {};
  const rows = payload?.threads || [];

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto px-5 lg:px-8 py-8" data-testid="email-inbox-page">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <Mail size={22} style={{ color: "var(--blue)" }} />
            <h1 className="text-2xl font-bold" style={{ color: "var(--navy)" }} data-testid="email-inbox-heading">Email inbox</h1>
          </div>
          <Button onClick={doSync} disabled={syncing || notConnected} data-testid="sync-now-btn" style={{ background: "var(--blue)", color: "white" }}>
            {syncing ? <><Loader2 size={14} className="mr-2 animate-spin"/>Syncing…</> : <><RefreshCw size={14} className="mr-2"/>Sync now</>}
          </Button>
        </div>

        {notConnected && (
          <div className="rounded-md border-l-4 p-4 mb-5" style={{ background: "#fef3c7", borderLeftColor: "#f59e0b" }} data-testid="not-connected-banner">
            <div className="text-sm" style={{ color: "#78350f" }}>
              Gmail is not connected for this scheme yet.
              <a href="/scheme/settings/connectors/gmail" className="ml-1 underline" style={{ color: "#78350f" }}>Connect Gmail →</a>
            </div>
          </div>
        )}

        <p className="text-sm mb-3" style={{ color: "var(--slate)" }}>
          {total} labelled email{total === 1 ? "" : "s"} ingested. Only emails tagged
          <code className="mx-1">OPSTRAIT Strata</code> in Gmail appear here.
        </p>

        {/* Category chips */}
        <div className="flex flex-wrap gap-2 mb-5" data-testid="category-chips">
          {CATEGORY_CHIPS.map((c) => {
            const active = category === c.key;
            const n = c.key ? (counts[c.key] || 0) : total;
            return (
              <button
                key={c.key || "all"}
                onClick={() => setCategory(c.key)}
                data-testid={`chip-${c.key || "all"}`}
                className="text-xs font-medium px-3 py-1.5 rounded-full border transition-all"
                style={{
                  background: active ? "var(--navy)" : "white",
                  color:      active ? "white" : "var(--slate)",
                  borderColor: active ? "var(--navy)" : "var(--border)",
                }}
              >
                {c.label} ({n})
              </button>
            );
          })}
        </div>

        {/* Threads */}
        <div className="rounded-xl bg-white border overflow-hidden" style={{ borderColor: "var(--border)" }}>
          {!payload ? (
            <div className="p-5 space-y-2">{[0,1,2].map(i => <Skeleton key={i} className="h-10 w-full" />)}</div>
          ) : rows.length === 0 ? (
            <div className="p-8 text-center text-sm" style={{ color: "var(--slate)" }} data-testid="email-inbox-empty">
              {notConnected ? "Connect Gmail to see emails here." : "No emails match this filter yet."}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead style={{ background: "#f8fafc" }}>
                <tr className="text-[11px] uppercase tracking-wider" style={{ color: "var(--slate)" }}>
                  <th className="text-left px-4 py-2 font-semibold">From</th>
                  <th className="text-left px-4 py-2 font-semibold">Subject / AI summary</th>
                  <th className="text-left px-4 py-2 font-semibold">Category</th>
                  <th className="text-left px-4 py-2 font-semibold">Last activity</th>
                  <th className="text-left px-4 py-2 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((t) => (
                  <tr key={t.id} className="border-t" style={{ borderColor: "var(--border)" }} data-testid={`email-row-${t.id}`}>
                    <td className="px-4 py-3 text-xs" style={{ color: "var(--navy)" }}>{t.from_email || "—"}</td>
                    <td className="px-4 py-3">
                      <div className="font-semibold" style={{ color: "var(--navy)" }}>{t.subject || "(no subject)"}</div>
                      {t.ai_summary && <div className="text-xs" style={{ color: "var(--slate)" }}>{t.ai_summary}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded"
                        style={{
                          background: `${CATEGORY_COLOR[t.category] || "#475569"}1a`,
                          color:      CATEGORY_COLOR[t.category] || "#475569",
                        }}
                      >
                        <Zap size={9}/> {t.category || "—"}
                        {t.ai_confidence != null && <span className="opacity-60 font-normal">{(t.ai_confidence*100).toFixed(0)}%</span>}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs" style={{ color: "var(--slate)" }}>{(t.last_message_at||"").slice(0,16).replace("T"," ")}</td>
                    <td className="px-4 py-3 text-xs font-medium" style={{ color: "var(--slate)" }}>{t.status || "NEW"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </AppShell>
  );
}
